// Shared "detail view" horizontal inset — operator items a+b, 2026-08-16.
//
// The catalog/marketing grid is full-width (the embed root deliberately has
// no cap — guarded by MarketplaceEmbed.test.tsx), but the DETAIL surfaces
// (product detail, cart, checkout, order confirmation) inset themselves
// 15%/15% at >=900px so they read as distinct panels against the full-width
// marketing UI. Below 900 the inset collapses to a small fixed gutter — a
// 360px viewport cannot give up 90px to percentages.
//
// One source of truth: every consumer calls detailInsetStyle() with its
// viewport width (useViewportWidth below), so the breakpoint and gutter can
// never drift between views.

import { useEffect, useState, type CSSProperties } from "react";
import type { TokenSet } from "../../embed-shared/tokens";

/** Viewport width at which detail views switch from fixed gutter to 15%/15%. */
export const DETAIL_INSET_BREAKPOINT = 900;

/**
 * Viewport width with resize tracking; jsdom-safe (reads innerWidth, listens
 * to resize). Formerly ProductDetailView's local hook — promoted here when
 * the inset grew to four consumers (cart/checkout/order-confirmation).
 * Deliberately NOT shared with ProductGrid's container-width hook: that one
 * measures a DOM node, this one the viewport — same shape, different source.
 */
export function useViewportWidth(): number {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

/** The inset padding shared by every detail view's root. */
export function detailInsetStyle(t: TokenSet, viewportWidth: number): CSSProperties {
  return {
    padding:
      viewportWidth >= DETAIL_INSET_BREAKPOINT ? "0 15%" : `0 ${t.spacing.lg}px`,
  };
}
